import { BaseService } from "src/services/baseService";
import { UserAccountAPIService } from "./userAccountAPIService";
import { sanitiseParams } from "src/utils/sanitiser";
import { ValidationError } from "src/errors";
import { PostUserAccountRequestBody, PatchUserAccountRequestBody, PostAccountRequestRequestBody } from "src/types";

class UserAccountService extends BaseService<UserAccountAPIService> {
  @sanitiseParams()
  async getUserAccountByEmail(userEmail: string) {
    return await this.apiService.getUserAccountByEmail(userEmail);
  }

  @sanitiseParams()
  async updateUserAccount(userId: string, requestBody: PatchUserAccountRequestBody) {
    const { supervisorEmails, vendorNames } = requestBody;

    if (supervisorEmails && new Set(supervisorEmails).size !== supervisorEmails.length) {
      throw new ValidationError("Duplicate supervisor emails", "supervisorEmails");
    }

    if (vendorNames && new Set(requestBody.vendorNames).size !== vendorNames.length) {
      throw new ValidationError("Duplicate vendor names", "vendorNames");
    }

    return await this.apiService.patchUserAccount(userId, requestBody);
  }

  @sanitiseParams()
  async deactivateUserAccount(userId: string) {
    return await this.apiService.patchUserAccountDeactivation(userId);
  }

  @sanitiseParams()
  async deleteUserAccount(userId: string) {
    return await this.apiService.deleteUserAccount(userId);
  }

  @sanitiseParams()
  async createUserAccount(requestBody: PostUserAccountRequestBody) {
    const { supervisorEmails, vendorNames } = requestBody;

    if (supervisorEmails && new Set(supervisorEmails).size !== supervisorEmails?.length) {
      throw new ValidationError("Duplicate supervisor emails", "supervisorEmails");
    }

    if (vendorNames && new Set(requestBody.vendorNames).size !== requestBody.vendorNames?.length) {
      throw new ValidationError("Duplicate vendor names", "vendorNames");
    }

    return await this.apiService.postUserAccount(requestBody);
  }

  @sanitiseParams()
  async requestAccountAccess(requestBody: PostAccountRequestRequestBody) {
    return await this.apiService.postAccountRequest(requestBody);
  }

  @sanitiseParams()
  async sendPasswordResetNotification(email: string, userName?: string) {
    return await this.apiService.postPasswordResetNotification({ email, userName });
  }
}

export { UserAccountService };
